import type { MechanicDefinition } from "./types";

export const turnStructure: MechanicDefinition = {
  id: "turn_structure",
  name: "Turn Structure (Legacy)",
  description: "Legacy turn-flow mechanic retained for backward compatibility. New worlds should use Time passage.",
  icon: "ListOrdered",
  category: "core",
  disabled: true,
  auditPasses: 1, // ⭐
  aiHints: {
    purpose: "Controls turn flow for multiplayer — free-form, round-robin, initiative-based, or concurrent. Also supports structured phases (Action → Resolution → Narration) and mandatory dice for all actions.",
    whenToUse: "Multiplayer games or any game where turn order matters. Also useful in solo play for structured phase flow or mandatory dice enforcement.",
    synergies: ["combat", "game_phases", "skill_checks", "dice"],
    conflicts: [],
    prerequisites: [],
    tags: ["turns", "multiplayer", "initiative", "flow", "core", "phases"],
    complexity: 1,
    exampleConfig: { turn_mode: "round_robin", phases_per_turn: true, mandatory_dice: false, time_limit_seconds: 0 },
    stateVariables: ["turn_order", "current_turn_player", "current_turn_index", "_turn_mode", "_current_phase"],
  },
  configSchema: [
    {
      key: "turn_mode",
      label: "Turn Mode",
      type: "select",
      options: [
        { value: "free_form", label: "Free-form (no enforced order)" },
        { value: "round_robin", label: "Round Robin (fixed rotation)" },
        { value: "initiative_order", label: "Initiative (rolled order)" },
        { value: "concurrent", label: "Concurrent (all act simultaneously)" },
      ],
      default: "free_form",
      description: "How player turns are organized",
    },
    {
      key: "phases_per_turn",
      label: "Structured Phases",
      type: "boolean",
      default: false,
      description: "Each turn follows Action → Resolution → Narration phases",
    },
    {
      key: "mandatory_dice",
      label: "Mandatory Dice for All Actions",
      type: "boolean",
      default: false,
      description: "Every meaningful action requires a dice roll (D&D style)",
    },
    {
      key: "time_limit_seconds",
      label: "Turn Time Limit (seconds)",
      type: "number",
      default: 0,
      description: "Real-time limit per turn (0 = unlimited)",
    },
  ],
  systemPromptSection: (config) => {
    const mode = config.turn_mode || "free_form";
    const phases = config.phases_per_turn || false;
    const mandatoryDice = config.mandatory_dice || false;
    const timeLimit = config.time_limit_seconds || 0;

    const modeDesc: Record<string, string> = {
      free_form: "Players act freely in any order. No turn enforcement.",
      round_robin: "Players take turns in fixed rotation. Use end_turn tool after each player's action resolves. Announce whose turn it is next.",
      initiative_order: "At the start of encounters, use set_turn_order to roll initiative for all participants. Players act in initiative order. Use end_turn to advance.",
      concurrent: "All players may act simultaneously. Resolve actions in the order received. No turn gating.",
    };

    const phaseDesc = phases
      ? "\n- TURN PHASES: Each turn follows three phases:\n  1. ACTION: Player declares their action\n  2. RESOLUTION: Dice are rolled, mechanics resolved\n  3. NARRATION: Describe the outcome and set up the next beat"
      : "";

    const diceDesc = mandatoryDice
      ? "\n- MANDATORY DICE: EVERY meaningful player action requires a dice roll. No auto-success. Even seemingly easy tasks require a roll — the dice tell the story."
      : "";

    const timeDesc = timeLimit > 0
      ? `\n- TIME LIMIT: Each player has ${timeLimit} seconds to act. If they don't respond, their turn is skipped.`
      : "";

    return `## TURN STRUCTURE (System Rule)
- Mode: ${mode}. ${modeDesc[mode]}${phaseDesc}${diceDesc}${timeDesc}
- TURN ADVANCEMENT: Not every message is a turn. A "turn" only advances when a meaningful action resolves (combat round, skill check, consequential decision). Pure dialogue, questions, looking around, or OOC chat do NOT advance the turn counter. The Director controls this via advance_turn. In round_robin/initiative modes, only end_turn advances turns.
- Track "current_turn_player" and "turn_order" in state variables.
- In multiplayer: announce whose turn it is at the start of each turn.`;
  },
  tools: [
    {
      type: "function",
      function: {
        name: "end_turn",
        description: "End the current player's turn and advance to the next player in the turn order",
        parameters: {
          type: "object",
          properties: {
            current_player: { type: "string", description: "Name of the player whose turn just ended" },
            summary: { type: "string", description: "Brief summary of what happened this turn" },
          },
          required: ["current_player"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "set_turn_order",
        description: "Set the turn order for all participants (used for initiative-based turns)",
        parameters: {
          type: "object",
          properties: {
            order: {
              type: "array",
              items: { type: "object", properties: { name: { type: "string" }, initiative: { type: "number" } }, required: ["name", "initiative"] },
              description: "Participants sorted by initiative (highest first)",
            },
          },
          required: ["order"],
        },
      },
    },
  ],
  widgets: [
    {
      id: "initiative_order",
      component: "InitiativeOrderWidget",
      label: "Initiative",
      icon: "ListOrdered",
      type: "sidebar_tab",
      dataBindings: {
        turnOrder: "variables.turn_order",
        currentPlayer: "variables.current_turn_player",
        currentIndex: "variables.current_turn_index",
      },
      visibility: "when_data",
      priority: 5,
    },
  ],
  initializeState: (config) => ({
    turn_order: [],
    current_turn_player: null,
    current_turn_index: 0,
    _turn_mode: config.turn_mode || "free_form",
    _current_phase: null,
  }),
};
